import React, { useState, useEffect } from 'react';
import { User } from '../types';
import { Zap, Infinity, Crown, Lock, Clock } from 'lucide-react';

interface ActivePromoEffectsProps {
    user: User | null;
    compact?: boolean;
}

const ActivePromoEffects: React.FC<ActivePromoEffectsProps> = ({ user, compact = false }) => {
    const [countdown, setCountdown] = useState<{ [key: string]: string }>({});

    useEffect(() => {
        if (!user?.activePromoEffects) return;

        const updateCountdowns = () => {
            const now = new Date();
            const newCountdowns: { [key: string]: string } = {};

            // Unlimited tokens countdown
            if (user.activePromoEffects.unlimitedTokensUntil) {
                const expiry = new Date(user.activePromoEffects.unlimitedTokensUntil);
                const remaining = expiry.getTime() - now.getTime();
                
                if (remaining > 0) {
                    const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
                    const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
                    
                    if (days > 0) {
                        newCountdowns.unlimitedTokens = `${days}d ${hours}h`;
                    } else if (hours > 0) {
                        newCountdowns.unlimitedTokens = `${hours}h ${minutes}m`;
                    } else {
                        newCountdowns.unlimitedTokens = `${minutes}m`;
                    }
                }
            }

            // Token multiplier countdown
            if (user.activePromoEffects.tokenMultiplierUntil) {
                const expiry = new Date(user.activePromoEffects.tokenMultiplierUntil);
                const remaining = expiry.getTime() - now.getTime();
                
                if (remaining > 0) {
                    const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
                    const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
                    
                    if (days > 0) {
                        newCountdowns.tokenMultiplier = `${days}d ${hours}h`;
                    } else if (hours > 0) {
                        newCountdowns.tokenMultiplier = `${hours}h ${minutes}m`;
                    } else {
                        newCountdowns.tokenMultiplier = `${minutes}m`;
                    }
                }
            }

            // Feature unlocks countdown
            if (user.activePromoEffects.featureUnlocksUntil) {
                const expiry = new Date(user.activePromoEffects.featureUnlocksUntil);
                const remaining = expiry.getTime() - now.getTime();
                
                if (remaining > 0) {
                    const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
                    const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
                    
                    if (days > 0) {
                        newCountdowns.featureUnlocks = `${days}d ${hours}h`;
                    } else if (hours > 0) {
                        newCountdowns.featureUnlocks = `${hours}h ${minutes}m`;
                    } else {
                        newCountdowns.featureUnlocks = `${minutes}m`;
                    }
                }
            }

            setCountdown(newCountdowns);
        };

        updateCountdowns();
        const interval = setInterval(updateCountdowns, 60000); // Update every minute

        return () => clearInterval(interval);
    }, [user?.activePromoEffects]);

    if (!user?.activePromoEffects) return null;

    const hasActiveEffects = 
        user.activePromoEffects.unlimitedTokensUntil ||
        user.activePromoEffects.tokenMultiplier ||
        (user.activePromoEffects.unlockedFeatures && user.activePromoEffects.unlockedFeatures.length > 0);

    if (!hasActiveEffects) return null;

    // Check if effects are still valid
    const now = new Date();
    const unlimitedTokensActive = user.activePromoEffects.unlimitedTokensUntil && 
        new Date(user.activePromoEffects.unlimitedTokensUntil) > now;
    const tokenMultiplierActive = user.activePromoEffects.tokenMultiplierUntil && 
        new Date(user.activePromoEffects.tokenMultiplierUntil) > now;
    const featuresActive = user.activePromoEffects.featureUnlocksUntil && 
        new Date(user.activePromoEffects.featureUnlocksUntil) > now;

    if (compact) {
        return (
            <div className="flex flex-wrap gap-1.5">
                {unlimitedTokensActive && (
                    <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full text-[9px] font-bold shadow-lg animate-pulse">
                        <Infinity size={10} />
                        <span>UNLIMITED</span>
                    </div>
                )}
                {tokenMultiplierActive && (
                    <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full text-[9px] font-bold shadow-lg">
                        <Zap size={10} />
                        <span>{user.activePromoEffects.tokenMultiplier}x</span>
                    </div>
                )}
                {featuresActive && user.activePromoEffects.unlockedFeatures && user.activePromoEffects.unlockedFeatures.length > 0 && (
                    <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-indigo-500 to-blue-500 text-white rounded-full text-[9px] font-bold shadow-lg">
                        <Lock size={10} />
                        <span>{user.activePromoEffects.unlockedFeatures.length}</span>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-2">
            {unlimitedTokensActive && (
                <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-400/30 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-purple-500/20 rounded-lg">
                                <Infinity size={14} className="text-purple-400" />
                            </div>
                            <span className="text-xs font-bold text-purple-300">Unlimited Tokens</span>
                        </div>
                        <div className="flex items-center gap-1 text-[10px] text-purple-400">
                            <Clock size={10} />
                            <span>{countdown.unlimitedTokens || 'Active'}</span>
                        </div>
                    </div>
                    <p className="text-[10px] text-purple-300/70">No token limits until expiry</p>
                </div>
            )}

            {tokenMultiplierActive && (
                <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-400/30 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-amber-500/20 rounded-lg">
                                <Zap size={14} className="text-amber-400" />
                            </div>
                            <span className="text-xs font-bold text-amber-300">{user.activePromoEffects.tokenMultiplier}x Token Boost</span>
                        </div>
                        <div className="flex items-center gap-1 text-[10px] text-amber-400">
                            <Clock size={10} />
                            <span>{countdown.tokenMultiplier || 'Active'}</span>
                        </div>
                    </div>
                    <p className="text-[10px] text-amber-300/70">Earn {user.activePromoEffects.tokenMultiplier}x tokens on refresh</p>
                </div>
            )}

            {featuresActive && user.activePromoEffects.unlockedFeatures && user.activePromoEffects.unlockedFeatures.length > 0 && (
                <div className="bg-gradient-to-r from-indigo-500/20 to-blue-500/20 border border-indigo-400/30 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-indigo-500/20 rounded-lg">
                                <Lock size={14} className="text-indigo-400" />
                            </div>
                            <span className="text-xs font-bold text-indigo-300">Features Unlocked</span>
                        </div>
                        <div className="flex items-center gap-1 text-[10px] text-indigo-400">
                            <Clock size={10} />
                            <span>{countdown.featureUnlocks || 'Active'}</span>
                        </div>
                    </div>
                    <p className="text-[10px] text-indigo-300/70">{user.activePromoEffects.unlockedFeatures.join(', ')}</p>
                </div>
            )}
        </div>
    );
};

export default ActivePromoEffects;
